import { trackAction } from '@cint/analytics';
import {
  BodyText,
  Card,
  CardContent,
  HeadingText,
  NextButton,
} from '@cint/wonderment';
import { useLocation, useNavigate } from 'react-router-dom';

import { EVENTS } from '@/analytics';
import { RESOURCES } from '@/utils/permissions';

import { Protected } from '../Protected';

function BoxBuilder({
  number,
  title,
  description,
}: {
  number: string;
  title: string;
  description: string;
}) {
  return (
    <Card variant="subtle">
      <CardContent className="flex flex-row gap-3">
        <BodyText size="sm">{number}</BodyText>
        <div className="flex-1 inline-flex flex-col justify-start items-start gap-3">
          <BodyText size="sm" fontWeight="bold">
            {title}
          </BodyText>
          <BodyText size="sm">{description}</BodyText>
        </div>
      </CardContent>
    </Card>
  );
}

export function NoMarketingCampaigns() {
  const navigate = useNavigate();
  const location = useLocation();

  function handleCreateCampaignClick() {
    trackAction(EVENTS.CAMPAIGN_CREATE);
    navigate(
      location.pathname.replace('marketingCampaigns', 'createMarketingCampaign')
    );
  }

  return (
    <div className="self-stretch py-4 inline-flex flex-col justify-start items-start gap-10">
      <div className="flex-1 inline-flex flex-col justify-center items-start gap-3">
        <HeadingText variant="h5" fontWeight="normal">
          Track your marketing campaign performance
        </HeadingText>
        <BodyText>
          Start tracking your online campaigns to refine your strategy and
          budget.
        </BodyText>
      </div>
      <div className="self-stretch flex flex-col justify-start items-start gap-4">
        <BodyText fontWeight="bold" size="lg">
          How it works
        </BodyText>
        <div className="self-stretch inline-flex justify-start items-start gap-3">
          <BoxBuilder
            number="1"
            title="Use an existing marketing campaign"
            description="Start with a campaign already running in Google Ads, Meta, blog articles, or other online platforms."
          />
          <BoxBuilder
            number="2"
            title="Create tracking setup"
            description="Configure your tracking provider, UTM parameters, and conversion event signals in Engage."
          />
          <BoxBuilder
            number="3"
            title="Optimize your strategy"
            description="Measure effectiveness to identify top-performing campaigns, reallocate budget, and maximize ROI."
          />
        </div>
      </div>
      <div className="self-stretch flex flex-col justify-start items-start gap-2">
        <Protected
          action={RESOURCES.marketing.actions.editcamp}
          resource={RESOURCES.marketing.id}
          showFallbackUI={false}
        >
          <NextButton
            text="Create tracking setup"
            variant="primary"
            size="xl"
            iconProps={{ name: 'Plus' }}
            data-testid="create-tracking-setup-button"
            onClick={handleCreateCampaignClick}
          />
        </Protected>
      </div>
    </div>
  );
}
